import React, { useState, useEffect } from 'react';
import { X, Plus, Lightbulb, Loader, MinusCircle, PlusCircle } from 'lucide-react';
import { useAppTheme } from '../../contexts/ThemeContext';

// ─────────────────────────────────────────────────────────────────────────────
// Static config
// ─────────────────────────────────────────────────────────────────────────────

const CATEGORY_PRESETS = {
  rental:      ['STUDIO', 'BEDSITTER','ONE ROOM', 'TWO ROOM', 'THREE ROOM', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM'],
  commercial:  ['OPEN PLAN', 'PRIVATE OFFICE', 'EXECUTIVE SUITE', 'MEETING ROOM', 'CO-WORKING SPACE', 'RETAIL SPACE', 'WAREHOUSE/STORE'],
  residential: ['STUDIO', 'BEDSITTER', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM', 'FOUR BEDROOM', 'FIVE BEDROOM', 'MAISONETTE', 'TOWNHOUSE', 'VILLA', 'PENTHOUSE'],
  airbnb:      ['STUDIO', 'ONE ROOM', 'TWO ROOM', 'THREE ROOM', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM'],
};

const AHP_TIER_PRESETS = {
  social:     ['ONE ROOM', 'TWO ROOM', 'THREE ROOM', 'STUDIO', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM'],
  affordable: ['ONE ROOM', 'TWO ROOM', 'THREE ROOM', 'STUDIO', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM'],
  market:     ['STUDIO', 'ONE BEDROOM', 'TWO BEDROOM', 'THREE BEDROOM'],
};

const AHP_TIER_COLORS = {
  social:     '#3B82F6',
  affordable: '#10B981',
  market:     '#8B5CF6',
};

// ─────────────────────────────────────────────────────────────────────────────
// Empty form state builder
// ─────────────────────────────────────────────────────────────────────────────

const emptyForm = (propertyType, category = null) => {
  const base = {
    // Common
    name:          category?.name || '',
    useCustomName: false,
    customName:    '',
    totalUnits:    category?.totalUnits?.toString() || '',
    description:   category?.description || [],
    newDescPoint:  '',
    url:           category?.url || '',

    // Rental / commercial / airbnb base rate
    baseRent:      category?.baseRent?.toString() || '',

    // For-sale (residential / ahp)
    propertyValue:    category?.propertyValue?.toString() || '',
    deposit:          category?.deposit?.toString() || '',
    installmentsCost: category?.installmentsCost?.toString() || '',
    fullSaleOnly:     category?.fullSaleOnly || false,

    // AHP
    ahpTier: category?.ahpTier || 'social',

    // Airbnb custom charges
    customCharges: category?.customCharges
      ? category.customCharges.map((c, i) => ({ ...c, _id: i }))
      : [],
  };

  // If editing and name doesn't match a preset, switch to custom mode
  if (category?.name) {
    const presets = propertyType === 'ahp'
      ? AHP_TIER_PRESETS[category.ahpTier || 'social'] || []
      : CATEGORY_PRESETS[propertyType] || [];
    if (!presets.includes(category.name)) {
      base.useCustomName = true;
      base.customName    = category.name;
      base.name          = '__custom__';
    }
  }

  return base;
};

// ─────────────────────────────────────────────────────────────────────────────
// CategoryDialog
// ─────────────────────────────────────────────────────────────────────────────

const CategoryDialog = ({ open, onClose, onSubmit, category, loading, propertyType = 'rental' }) => {
  const { palette, Colors } = useAppTheme();
  const isEditMode = !!category;

  const P      = Colors.primary.main;
  const bg     = palette.background;
  const txt    = palette.text;
  const bdr    = palette.border;

  const [form, setForm]               = useState(() => emptyForm(propertyType, category));
  const [errors, setErrors]           = useState({});
  const [chargeNextId, setChargeNextId] = useState(100);

  useEffect(() => {
    if (open) {
      setForm(emptyForm(propertyType, category));
      setErrors({});
    }
  }, [open, category, propertyType]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  // AHP tier change resets name selection
  const handleTierChange = (tier) => {
    setForm(f => ({ ...f, ahpTier: tier, name: '', useCustomName: false, customName: '' }));
  };

  // Description bullet points
  // Splits on newlines so a pasted multi-line block becomes multiple points
  // instead of one long string. Also tolerates lines that already start
  // with manual bullets (-, *, •) or numbering (1., 1)) by stripping them.
  const splitIntoPoints = (raw) =>
    raw
      .split('\n')
      .map(line => line.trim().replace(/^([-*•]|\d+[.)])\s*/, ''))
      .map(line => line.trim())
      .filter(Boolean);

  const addDescPoint = () => {
    const points = splitIntoPoints(form.newDescPoint);
    if (points.length === 0) return;
    setForm(f => ({ ...f, description: [...f.description, ...points], newDescPoint: '' }));
  };
  const removeDescPoint = (i) =>
    setForm(f => ({ ...f, description: f.description.filter((_, idx) => idx !== i) }));

  // Airbnb custom charges
  const addCharge = () => {
    setForm(f => ({
      ...f,
      customCharges: [...f.customCharges, { _id: chargeNextId, type: '', description: '', amount: '' }],
    }));
    setChargeNextId(n => n + 1);
  };
  const updateCharge = (id, key, val) =>
    setForm(f => ({ ...f, customCharges: f.customCharges.map(c => c._id === id ? { ...c, [key]: val } : c) }));
  const removeCharge = (id) =>
    setForm(f => ({ ...f, customCharges: f.customCharges.filter(c => c._id !== id) }));

  // Validation
  const validate = () => {
    const e = {};
    const resolvedName = form.useCustomName ? form.customName.trim() : form.name;
    if (!resolvedName || resolvedName === '__custom__') e.name = 'Category name is required';

    if (!isEditMode) {
      if (!form.totalUnits || isNaN(form.totalUnits) || parseInt(form.totalUnits) < 1)
        e.totalUnits = 'Enter a valid number of units (min 1)';
    }

    if (['rental', 'commercial'].includes(propertyType)) {
      if (!form.baseRent || isNaN(form.baseRent) || parseFloat(form.baseRent) < 0)
        e.baseRent = 'Enter a valid monthly rent';
    }
    if (propertyType === 'airbnb') {
      if (!form.baseRent || isNaN(form.baseRent) || parseFloat(form.baseRent) < 0)
        e.baseRent = 'Enter a valid nightly rate';
    }
    if (['residential', 'ahp'].includes(propertyType)) {
      if (!form.propertyValue || isNaN(form.propertyValue) || parseFloat(form.propertyValue) < 0)
        e.propertyValue = 'Enter a valid property value';
      if (!form.fullSaleOnly) {
        if (!form.deposit || isNaN(form.deposit) || parseFloat(form.deposit) < 0)
          e.deposit = 'Enter a valid deposit amount';
        if (!form.installmentsCost || isNaN(form.installmentsCost) || parseFloat(form.installmentsCost) < 0)
          e.installmentsCost = 'Enter a valid monthly installment';
      }
    }
    if (propertyType === 'ahp' && !form.ahpTier) e.ahpTier = 'Select an AHP tier';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;

    const resolvedName = form.useCustomName ? form.customName.trim() : form.name;

    const payload = {
      name:        resolvedName,
      description: form.description.length > 0 ? form.description : null,
      url:         form.url.trim() || null,
    };

    if (!isEditMode) {
      payload.totalUnits = parseInt(form.totalUnits, 10);
    }

    if (['rental', 'commercial', 'airbnb'].includes(propertyType)) {
      payload.baseRent = parseFloat(form.baseRent);
    }
    if (propertyType === 'airbnb') {
      payload.customCharges = form.customCharges
        .filter(c => c.type.trim())
        .map(({ type, description, amount }) => ({ type, description, amount: parseFloat(amount) || 0 }));
    }
    if (['residential', 'ahp'].includes(propertyType)) {
      payload.propertyValue = parseFloat(form.propertyValue);
      payload.fullSaleOnly  = form.fullSaleOnly;
      if (!form.fullSaleOnly) {
        payload.deposit          = parseFloat(form.deposit);
        payload.installmentsCost = parseFloat(form.installmentsCost);
      }
    }
    if (propertyType === 'ahp') {
      payload.ahpTier = form.ahpTier;
    }

    onSubmit(payload);
  };

  if (!open) return null;

  // Styles (matching original style patterns)
  const focusSt    = { outline: 'none', borderColor: P, boxShadow: `0 0 0 2px ${P}30` };
  const inputSt    = { backgroundColor: bg.tertiary, color: txt.primary, border: `1px solid ${bdr}` };
  const errInputSt = { ...inputSt, border: `1px solid ${Colors.status.error}` };
  const errText    = { color: Colors.status.error, fontSize: '12px', marginTop: '4px' };

  const fieldFocus = (e) => Object.assign(e.target.style, focusSt);
  const fieldBlur  = (e) => {
    e.target.style.boxShadow   = '';
    e.target.style.borderColor = errors[e.target.name] ? Colors.status.error : bdr;
  };

  // Preset list for current property type / AHP tier
  const presetList = propertyType === 'ahp'
    ? AHP_TIER_PRESETS[form.ahpTier] || []
    : CATEGORY_PRESETS[propertyType] || [];

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-[9999]"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
      <div className="rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="sticky top-0 px-6 py-4 rounded-t-xl"
          style={{ backgroundColor: bg.secondary, borderBottom: `1px solid ${bdr}` }}>
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold" style={{ color: txt.primary }}>
              {isEditMode ? 'Edit Category' : 'Add New Category'}
            </h2>
            <button onClick={onClose} style={{ color: txt.muted }}><X className="w-6 h-6" /></button>
          </div>
        </div>

        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="p-6 space-y-5">

          {/* Hint box — add mode only */}
          {!isEditMode && (
            <div className="rounded-lg p-4" style={{ backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
              <div className="flex items-start gap-3">
                <Lightbulb className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: P }} />
                <div className="text-sm space-y-1">
                  <p className="font-semibold" style={{ color: txt.primary }}>Quick Tip:</p>
                  <p style={{ color: txt.secondary }}>
                    Select a preset category name or choose &quot;+ Custom&quot; to enter your own.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── AHP Tier picker ──────────────────────────────────────────── */}
          {propertyType === 'ahp' && (
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                AHP Tier <span style={{ color: Colors.status.error }}>*</span>
              </label>
              <div className="grid grid-cols-3 gap-2">
                {['social', 'affordable', 'market'].map(tier => (
                  <button key={tier} type="button"
                    onClick={() => handleTierChange(tier)}
                    className="py-2.5 px-3 rounded-lg font-medium text-sm capitalize transition border-2"
                    style={{
                      borderColor:     form.ahpTier === tier ? AHP_TIER_COLORS[tier] : bdr,
                      backgroundColor: form.ahpTier === tier ? `${AHP_TIER_COLORS[tier]}15` : bg.tertiary,
                      color:           form.ahpTier === tier ? AHP_TIER_COLORS[tier] : txt.secondary,
                    }}>
                    {tier}
                  </button>
                ))}
              </div>
              {errors.ahpTier && <p style={errText}>{errors.ahpTier}</p>}
            </div>
          )}

          {/* ── Category Name ────────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
              Category Name <span style={{ color: Colors.status.error }}>*</span>
            </label>

            {/* Preset chips — shown when not in custom mode */}
            {!form.useCustomName && (
              <div className="flex flex-wrap gap-2 mb-3">
                {presetList.map(preset => (
                  <button key={preset} type="button"
                    onClick={() => set('name', preset)}
                    className="px-3 py-1.5 rounded-lg text-sm font-medium transition border"
                    style={{
                      borderColor:     form.name === preset ? P : bdr,
                      backgroundColor: form.name === preset ? `${P}15` : bg.tertiary,
                      color:           form.name === preset ? P : txt.secondary,
                    }}>
                    {preset}
                  </button>
                ))}
                <button type="button"
                  onClick={() => { set('name', '__custom__'); set('useCustomName', true); }}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition border"
                  style={{ borderColor: bdr, backgroundColor: bg.tertiary, color: txt.muted }}>
                  + Custom
                </button>
              </div>
            )}

            {/* Custom name input */}
            {form.useCustomName && (
              <div className="flex gap-2">
                <input name="customName"
                  type="text"
                  value={form.customName}
                  onChange={e => set('customName', e.target.value)}
                  placeholder="e.g., DUPLEX, COTTAGE, LOFT"
                  className="flex-1 px-4 py-2.5 rounded-lg focus:outline-none transition"
                  style={errors.name ? errInputSt : inputSt}
                  onFocus={fieldFocus} onBlur={fieldBlur} />
                <button type="button"
                  onClick={() => { set('useCustomName', false); set('customName', ''); set('name', ''); }}
                  className="px-3 py-2 rounded-lg text-sm transition"
                  style={{ backgroundColor: bg.tertiary, color: txt.muted, border: `1px solid ${bdr}` }}>
                  Presets
                </button>
              </div>
            )}
            {errors.name && <p style={errText}>{errors.name}</p>}
          </div>

          {/* ── Rental / Commercial: Monthly Rent ───────────────────────── */}
          {['rental', 'commercial'].includes(propertyType) && (
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                Base Rent (KES/month) <span style={{ color: Colors.status.error }}>*</span>
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-medium" style={{ color: txt.secondary }}>KES</span>
                <input name="baseRent" type="number" min="0"
                  value={form.baseRent}
                  onChange={e => set('baseRent', e.target.value)}
                  placeholder="15000"
                  className="w-full pl-16 pr-4 py-2.5 rounded-lg focus:outline-none transition"
                  style={errors.baseRent ? errInputSt : inputSt}
                  onFocus={fieldFocus} onBlur={fieldBlur} />
              </div>
              {errors.baseRent && <p style={errText}>{errors.baseRent}</p>}
            </div>
          )}

          {/* ── Airbnb: Nightly Rate + Custom Charges ───────────────────── */}
          {propertyType === 'airbnb' && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                  Nightly Rate (KES) <span style={{ color: Colors.status.error }}>*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-medium" style={{ color: txt.secondary }}>KES</span>
                  <input name="baseRent" type="number" min="0"
                    value={form.baseRent}
                    onChange={e => set('baseRent', e.target.value)}
                    placeholder="5000"
                    className="w-full pl-16 pr-4 py-2.5 rounded-lg focus:outline-none transition"
                    style={errors.baseRent ? errInputSt : inputSt}
                    onFocus={fieldFocus} onBlur={fieldBlur} />
                </div>
                {errors.baseRent && <p style={errText}>{errors.baseRent}</p>}
              </div>

              {/* Custom charges */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-semibold" style={{ color: txt.secondary }}>
                    Other Charges <span className="font-normal" style={{ color: txt.muted }}>(optional)</span>
                  </label>
                  <button type="button" onClick={addCharge}
                    className="flex items-center gap-1.5 text-sm font-medium px-3 py-1.5 rounded-lg transition"
                    style={{ color: P, backgroundColor: `${P}10`, border: `1px solid ${P}30` }}>
                    <PlusCircle className="w-4 h-4" /> Add Charge
                  </button>
                </div>
                {form.customCharges.length === 0 ? (
                  <p className="text-sm py-3 text-center rounded-lg"
                    style={{ color: txt.muted, backgroundColor: bg.tertiary, border: `1px dashed ${bdr}` }}>
                    No custom charges added
                  </p>
                ) : (
                  <div className="space-y-2">
                    {form.customCharges.map(charge => (
                      <div key={charge._id} className="p-3 rounded-lg"
                        style={{ backgroundColor: bg.tertiary, border: `1px solid ${bdr}` }}>
                        <div className="grid grid-cols-3 gap-2">
                          <input
                            style={{ backgroundColor: bg.secondary, color: txt.primary, border: `1px solid ${bdr}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none' }}
                            placeholder="Type (e.g. Cleaning)"
                            value={charge.type}
                            onChange={e => updateCharge(charge._id, 'type', e.target.value)}
                            onFocus={fieldFocus} onBlur={fieldBlur} />
                          <input
                            style={{ backgroundColor: bg.secondary, color: txt.primary, border: `1px solid ${bdr}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none' }}
                            placeholder="Description"
                            value={charge.description}
                            onChange={e => updateCharge(charge._id, 'description', e.target.value)}
                            onFocus={fieldFocus} onBlur={fieldBlur} />
                          <div className="flex gap-1.5">
                            <input type="number" min="0"
                              style={{ backgroundColor: bg.secondary, color: txt.primary, border: `1px solid ${bdr}`, borderRadius: '8px', padding: '8px 10px', fontSize: '13px', outline: 'none', flex: 1 }}
                              placeholder="KES"
                              value={charge.amount}
                              onChange={e => updateCharge(charge._id, 'amount', e.target.value)}
                              onFocus={fieldFocus} onBlur={fieldBlur} />
                            <button type="button" onClick={() => removeCharge(charge._id)}
                              className="p-2 rounded-lg flex-shrink-0 transition"
                              style={{ color: Colors.status.error }}
                              onMouseEnter={e => e.currentTarget.style.backgroundColor = `${Colors.status.error}15`}
                              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                              <MinusCircle className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Residential / AHP: For-sale pricing ─────────────────────── */}
          {['residential', 'ahp'].includes(propertyType) && (
            <>
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                  Property Value (KES) <span style={{ color: Colors.status.error }}>*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 font-medium" style={{ color: txt.secondary }}>KES</span>
                  <input name="propertyValue" type="number" min="0"
                    value={form.propertyValue}
                    onChange={e => set('propertyValue', e.target.value)}
                    placeholder="2500000"
                    className="w-full pl-16 pr-4 py-2.5 rounded-lg focus:outline-none transition"
                    style={errors.propertyValue ? errInputSt : inputSt}
                    onFocus={fieldFocus} onBlur={fieldBlur} />
                </div>
                {errors.propertyValue && <p style={errText}>{errors.propertyValue}</p>}
              </div>

              {/* Full sale toggle */}
              <div className="flex items-center gap-3 p-3 rounded-lg"
                style={{ backgroundColor: bg.tertiary, border: `1px solid ${bdr}` }}>
                <button type="button"
                  onClick={() => set('fullSaleOnly', !form.fullSaleOnly)}
                  className="w-11 h-6 rounded-full transition-colors flex-shrink-0 relative"
                  style={{ backgroundColor: form.fullSaleOnly ? P : bdr }}>
                  <span className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform"
                    style={{ transform: form.fullSaleOnly ? 'translateX(20px)' : 'translateX(2px)' }} />
                </button>
                <div>
                  <p className="text-sm font-medium" style={{ color: txt.primary }}>Full Sale Only</p>
                  <p className="text-xs" style={{ color: txt.muted }}>No deposit or installment plan available</p>
                </div>
              </div>

              {/* Deposit + Installments */}
              {!form.fullSaleOnly && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                      Deposit (KES) <span style={{ color: Colors.status.error }}>*</span>
                    </label>
                    <input name="deposit" type="number" min="0"
                      value={form.deposit}
                      onChange={e => set('deposit', e.target.value)}
                      placeholder="250000"
                      className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition"
                      style={errors.deposit ? errInputSt : inputSt}
                      onFocus={fieldFocus} onBlur={fieldBlur} />
                    {errors.deposit && <p style={errText}>{errors.deposit}</p>}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                      Monthly Installment (KES) <span style={{ color: Colors.status.error }}>*</span>
                    </label>
                    <input name="installmentsCost" type="number" min="0"
                      value={form.installmentsCost}
                      onChange={e => set('installmentsCost', e.target.value)}
                      placeholder="15000"
                      className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition"
                      style={errors.installmentsCost ? errInputSt : inputSt}
                      onFocus={fieldFocus} onBlur={fieldBlur} />
                    {errors.installmentsCost && <p style={errText}>{errors.installmentsCost}</p>}
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── Total Units — add mode only ──────────────────────────────── */}
          {!isEditMode && (
            <div>
              <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
                Number of Units <span style={{ color: Colors.status.error }}>*</span>
              </label>
              <input name="totalUnits" type="number" min="1"
                value={form.totalUnits}
                onChange={e => set('totalUnits', e.target.value)}
                placeholder="e.g., 10"
                className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition"
                style={errors.totalUnits ? errInputSt : inputSt}
                onFocus={fieldFocus} onBlur={fieldBlur} />
              {errors.totalUnits
                ? <p style={errText}>{errors.totalUnits}</p>
                : <p className="mt-1 text-xs" style={{ color: txt.muted }}>You can add more units later from the category view</p>
              }
            </div>
          )}

          {/* ── Description Points ───────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
              Features &amp; Description <span className="font-normal" style={{ color: txt.muted }}>(Optional)</span>
            </label>
            <p className="text-xs mb-3" style={{ color: txt.muted }}>
              Add key features and amenities that make this category stand out
            </p>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={form.newDescPoint}
                  onChange={e => set('newDescPoint', e.target.value)}
                  onKeyPress={e => { if (e.key === 'Enter') { e.preventDefault(); addDescPoint(); } }}
                  onPaste={e => {
                    const pasted = e.clipboardData.getData('text');
                    if (pasted.includes('\n')) {
                      e.preventDefault();
                      const points = splitIntoPoints(pasted);
                      if (points.length > 0) {
                        setForm(f => ({ ...f, description: [...f.description, ...points], newDescPoint: '' }));
                      }
                    }
                    // Single-line paste: let it land in the input normally
                  }}
                  placeholder="e.g., Spacious living room with natural lighting"
                  className="flex-1 px-4 py-2.5 rounded-lg focus:outline-none transition"
                  style={inputSt}
                  onFocus={fieldFocus}
                  onBlur={e => { e.target.style.boxShadow = ''; e.target.style.borderColor = bdr; }} />
                <button type="button" onClick={addDescPoint}
                  className="text-white px-4 py-2.5 rounded-lg font-semibold transition flex items-center gap-2"
                  style={{ backgroundColor: P }}>
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              {form.description.length > 0 && (
                <div className="rounded-lg p-3 space-y-2" style={{ backgroundColor: bg.tertiary }}>
                  {form.description.map((point, i) => (
                    <div key={i} className="flex items-start gap-2 p-3 rounded-lg group transition"
                      style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}` }}>
                      <div className="mt-1.5">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: P }} />
                      </div>
                      <p className="flex-1 text-sm leading-relaxed" style={{ color: txt.primary }}>{point}</p>
                      <button type="button" onClick={() => removeDescPoint(i)}
                        className="text-gray-400 hover:text-red-600 transition">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ── URL ──────────────────────────────────────────────────────── */}
          <div>
            <label className="block text-sm font-semibold mb-2" style={{ color: txt.secondary }}>
              Additional Information URL <span className="font-normal" style={{ color: txt.muted }}>(Optional)</span>
            </label>
            <input type="url"
              value={form.url}
              onChange={e => set('url', e.target.value)}
              placeholder="https://example.com/unit-details"
              className="w-full px-4 py-2.5 rounded-lg focus:outline-none transition"
              style={inputSt}
              onFocus={fieldFocus}
              onBlur={e => { e.target.style.boxShadow = ''; e.target.style.borderColor = bdr; }} />
            <p className="mt-1 text-xs" style={{ color: txt.muted }}>
              Link to virtual tour, photo gallery, or additional details
            </p>
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────────────────── */}
        <div className="sticky bottom-0 px-6 py-4 flex justify-end gap-3 rounded-b-xl"
          style={{ backgroundColor: bg.tertiary, borderTop: `1px solid ${bdr}` }}>
          <button type="button" onClick={onClose}
            className="px-6 py-2.5 font-semibold rounded-lg transition"
            style={{ backgroundColor: bg.secondary, border: `1px solid ${bdr}`, color: txt.primary }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={loading}
            className="text-white px-6 py-2.5 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex items-center gap-2"
            style={{ backgroundColor: P }}>
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                {isEditMode ? 'Updating...' : 'Creating...'}
              </>
            ) : (isEditMode ? 'Update Category' : 'Create Category')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CategoryDialog;